import { useCallback, useEffect, useState } from 'react';
import { useBeforeUnload } from 'react-router-dom';
import { Attachment } from '../../../types';
import { isUnsentFile, stageAttachments } from '../../../store/StagedAttachment';
import { ATTACHMENT_UNSUPPORTED_TYPES, RequestSnapshot } from './serviceRequestModel';

/**
 * Files staged on the active tab until Submit: dropped when the request type cannot carry them, and the page
 * warns before unload while any tab holds an unsent file.
 */
export function useStagedAttachments(requestType: string, requests: RequestSnapshot[], activeTab: number) {
  const [stagedAttachments, setStagedAttachments] = useState<Attachment[]>([]);
  const attachmentsSupported = !ATTACHMENT_UNSUPPORTED_TYPES.includes(requestType);

  // Keeping them would strand files in a request that can never carry them, behind a hidden control.
  useEffect(() => {
    if (!attachmentsSupported) setStagedAttachments([]);
  }, [attachmentsSupported]);

  // The queue below sees only the active tab, and this form keeps files on every tab until Submit.
  const hasUnsentFiles =
    stagedAttachments.some(isUnsentFile) ||
    requests.some((request, index) => index !== activeTab && request.attachments.some(isUnsentFile));
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasUnsentFiles) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [hasUnsentFiles]
    )
  );

  const addStagedAttachments = (files: File[]) =>
    setStagedAttachments((current) => [...current, ...stageAttachments(files)]);

  const updateStagedAttachment = (updated: Attachment) =>
    setStagedAttachments((current) =>
      current.map((attachment) => (attachment.id === updated.id ? updated : attachment))
    );

  const removeStagedAttachment = (attachmentId: string) =>
    setStagedAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));

  // Dropping the row would hide a transfer that keeps running and still lands the file.
  const cancelStagedUpload = (attachmentId: string) =>
    stagedAttachments.find((attachment) => attachment.id === attachmentId)?.cancelSource.cancel();

  const requeueStagedAttachment = (attachmentId: string) =>
    setStagedAttachments((current) =>
      current.map((attachment) =>
        attachment.id === attachmentId ? { ...attachment, status: 'queued', progress: 0 } : attachment
      )
    );

  return {
    stagedAttachments,
    setStagedAttachments,
    attachmentsSupported,
    addStagedAttachments,
    updateStagedAttachment,
    removeStagedAttachment,
    cancelStagedUpload,
    requeueStagedAttachment
  };
}
